import { VisibilityOff, Visibility } from "@mui/icons-material";
import { Box, Card, CardContent, CardActions, Typography, Avatar, TextField, Button, Icon, useTheme, FormControl, OutlinedInput, InputLabel, InputAdornment, IconButton, Checkbox, FormControlLabel, CircularProgress, FormHelperText } from "@mui/material";
import { useState } from 'react';
import * as yup from 'yup';

import { useAuthContext } from "../../contexts";
import { Environment } from "../../environment";
import { LayoutBaseDePagina } from "../../layouts";
import { AuthService } from "../../services/api/usuario/auth/AuthService";
import { AlertSnackBar } from "../alert/AlertSnackBar";

interface ILoginProps {
    children: React.ReactNode;
}

const loginSchema = yup.object().shape({
    email: yup.string().required('Digite o email').email('Digite um email válido'),
    password: yup.string().required('Digite a senha'),
})
const forgotPasswordSchema = yup.object().shape({
    email: yup.string().required('Digite o email').email('Digite um email válido'),
})
export const Login: React.FC<ILoginProps> = ({ children }) => {
    const theme = useTheme();
    const { isAuthenticated, login } = useAuthContext();
    const [isLoading, setIsLoading] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [esqueceuSenha, setEsqueceuSenha] = useState(false);

    const [alertSnackbar, setAlertSnackbar] = useState<{ tipoAlert: string, mensagem: string } | undefined>(undefined);
    const [duracaoAlert, setDuracaoAlert] = useState<number>(Environment.DURACAO_ALERT + 500);

    const handleSubmit = () => {
        if (!esqueceuSenha) {
            loginSchema.validate({ email, password }, { abortEarly: false }).then(dadosValidados => {
                setIsLoading(true);
                login(dadosValidados.email, dadosValidados.password).then(result => {
                    setIsLoading(false);
                    if (result instanceof Error) {
                        setAlertSnackbar({ tipoAlert: "erro", mensagem: result.message });
                        setTimeout(() => {
                            setAlertSnackbar(undefined);
                        }, duracaoAlert);
                        setPassword("");
                    } else {
                        setEmail("");
                        setPassword("");
                        //alert - colocar um texto de logado com sucesso, mas talvez não seja necessário
                    }
                });
            }).catch((errors: yup.ValidationError) => {
                errors.inner.forEach(error => {
                    if (error.path === 'email') setEmailError(error.message);
                    if (error.path === 'password') setPasswordError(error.message);
                });
            });
        } else {
            forgotPasswordSchema.validate({ email }, { abortEarly: false }).then(dadosValidados => {
                setIsLoading(true);
                AuthService.forgotPassword(dadosValidados.email).then(result => {
                    setIsLoading(false);
                    if (result instanceof Error) {//verificar se deu erro
                        setAlertSnackbar({ tipoAlert: "erro", mensagem: result.message });
                        setTimeout(() => {
                            setAlertSnackbar(undefined);
                        }, duracaoAlert);
                    } else {
                        setAlertSnackbar({ tipoAlert: "sucesso", mensagem: "Uma nova senha foi enviada para o email " + email});
                        setTimeout(() => {
                            setAlertSnackbar(undefined);
                        }, duracaoAlert);
                    }
                });
            }).catch((errors: yup.ValidationError) => {
                errors.inner.forEach(error => {
                    if (error.path === 'email') {
                        setEmailError(error.message);
                    } else if (error.path === 'password') {
                        setPasswordError(error.message);
                    }
                });
            });
        }
    }

    const paperStyle = {
        padding: 20,
        maxHeight: '70vh',
        width: 280,
        margin: "10px auto",
        borderRadius: '10px',
    }
    const avatarStyle = { backgroundColor: theme.palette.primary.main };

    if (isAuthenticated) return (
        <>{children}</>
    );

    return (
        <LayoutBaseDePagina titulo="" barraDeFerramentas={null}>
            <Card style={paperStyle}>
                <CardContent>
                    <Box display='flex' flexDirection='column' gap={2} width={250}>
                        <Box width='100%' display='flex' justifyContent='center'> <Avatar style={avatarStyle}> <Icon>person</Icon> </Avatar> </Box>
                        <Typography variant='h6' align='center'> {esqueceuSenha ? 'Recuperar conta' : 'Fazer login'} </Typography>
                        <Typography variant='body2' align='center' mt={-1} mb={2}> {esqueceuSenha ? 'Um email com a nova senha será enviado para você' : 'Ir a Zdoc'} </Typography>

                        <TextField
                            fullWidth
                            label="Email"
                            placeholder="Digite o email..."
                            type='email'
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            onKeyDown={e => setEmailError('')}
                            disabled={isLoading}
                            error={!!emailError}
                            helperText={emailError}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSubmit();
                                    e.preventDefault();
                                }
                            }}
                        />
                            {alertSnackbar !== undefined && (
                             <>
                                {alertSnackbar.tipoAlert === "sucesso" && (
                                    <AlertSnackBar gravidade="success" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
                                )}
                                {alertSnackbar.tipoAlert === "erro" && (
                                    <AlertSnackBar gravidade="error" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
                                )}
                                {alertSnackbar.tipoAlert === "aviso" && (
                                    <AlertSnackBar gravidade="warning" mensagem={alertSnackbar.mensagem} returnedDuration={duracao => setDuracaoAlert(duracao + 500)} />
                                )}
                            </>
                            )}


                        {!esqueceuSenha && (
                            <FormControl variant="outlined">
                                <InputLabel htmlFor="campoSenha"> Senha </InputLabel>
                                <OutlinedInput
                                    id="campoSenha"
                                    label="Senha"
                                    placeholder="Digite a senha..."
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    onKeyDown={e => setPasswordError('')}
                                    disabled={isLoading}
                                    error={!!passwordError}
                                    autoComplete='off'
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSubmit();
                                            e.preventDefault();
                                        }
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={e => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                {!!passwordError && (
                                    <FormHelperText error id="accountId-error">
                                        {passwordError}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        )}
                    </Box>
                </CardContent>

                <CardActions>
                    <Box width='100%' display='flex' flexDirection='column' gap={2}>
                        <FormControl>
                            <FormControlLabel control={<Checkbox checked={esqueceuSenha} onChange={e => setEsqueceuSenha(!esqueceuSenha)} disabled={isLoading} />} label="Esqueceu a senha?" />
                        </FormControl>
                        <Button
                            variant='contained'
                            onClick={handleSubmit}
                            disabled={isLoading}
                            endIcon={isLoading ? <CircularProgress variant='indeterminate' color='inherit' size={20} /> : undefined}
                        >
                            {esqueceuSenha ? 'Refazer senha' : 'Entrar'}
                        </Button>
                    </Box>
                </CardActions>
            </Card>
        </LayoutBaseDePagina>
    );
}